import React, { useState, useMemo } from 'react';
import { Table, Button, Modal, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { formatPrecio, generarTituloProducto } from '../../utils/formatters';
import { ROUTES, labelCajaTipo, DESTACADOS_MAX } from '../../constants';
import { actualizarProducto, eliminarProducto } from '../../services/productoService';
import { eliminarImagen } from '../../services/imagenService';
import { obtenerImagenesProducto } from '../../utils/imageUtils';

/**
 * Componente de tabla para mostrar productos
 * @param {Object} props
 * @param {Array} props.productos - Lista de productos a mostrar
 * @param {Function} props.onEliminar - Callback cuando se elimina un producto
 */
function TablaProductos({ productos, productosTotales, onEliminar }) {
  const navigate = useNavigate();
  const [showConfirmEliminar, setShowConfirmEliminar] = useState(false);
  const [productoAEliminar, setProductoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [destacadoOverrides, setDestacadoOverrides] = useState({});
  const [updatingDestacado, setUpdatingDestacado] = useState(() => new Set());
  const [errorDestacado, setErrorDestacado] = useState(null);

  const handleEditar = (id) => {
    navigate(`${ROUTES.ADMIN_PRODUCTOS}/editar/${id}`);
  };

  const handleEliminar = (producto) => {
    setProductoAEliminar(producto);
    setShowConfirmEliminar(true);
    setErrorEliminar(null);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDestacadoChange = async (producto, destacado) => {
    const anterior = producto.destacado === true;
    if (destacado === anterior) return;

    if (destacado === true && destacadosCount >= DESTACADOS_MAX) {
      setErrorDestacado(
        `Ya hay ${DESTACADOS_MAX} productos destacados. Desmarcá uno para marcar otro.`
      );
      return;
    }

    setErrorDestacado(null);
    setUpdatingDestacado((prev) => new Set(prev).add(producto.id));
    setDestacadoOverrides((prev) => ({ ...prev, [producto.id]: destacado }));

    try {
      await actualizarProducto(producto.id, { destacado });
    } catch (error) {
      setDestacadoOverrides((prev) => ({ ...prev, [producto.id]: anterior }));
      setErrorDestacado(error.message || 'No se pudo actualizar el destacado');
    } finally {
      setUpdatingDestacado((prev) => {
        const next = new Set(prev);
        next.delete(producto.id);
        return next;
      });
    }
  };

  const productosConDestacado = useMemo(() => {
    return productos.map((producto) => (
      Object.prototype.hasOwnProperty.call(destacadoOverrides, producto.id)
        ? { ...producto, destacado: destacadoOverrides[producto.id] }
        : producto
    ));
  }, [productos, destacadoOverrides]);

  const destacadosCount = useMemo(() => {
    const source = productosTotales || productos;
    return source.filter((producto) => {
      if (Object.prototype.hasOwnProperty.call(destacadoOverrides, producto.id)) {
        return destacadoOverrides[producto.id] === true;
      }
      return producto.destacado === true;
    }).length;
  }, [productos, productosTotales, destacadoOverrides]);

  const limiteDestacados = destacadosCount >= DESTACADOS_MAX;

  const productosOrdenados = useMemo(() => {
    if (!sortColumn) return productosConDestacado;

    return [...productosConDestacado].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === 'precio') {
        aValue = aValue != null ? Number(aValue) : -Infinity;
        bValue = bValue != null ? Number(bValue) : -Infinity;
      } else if (sortColumn === 'destacado') {
        aValue = a.destacado === true ? 1 : 0;
        bValue = b.destacado === true ? 1 : 0;
      } else if (sortColumn === 'titulo') {
        aValue = generarTituloProducto(a).toLowerCase();
        bValue = generarTituloProducto(b).toLowerCase();
      } else {
        aValue = aValue != null ? String(aValue).toLowerCase() : '';
        bValue = bValue != null ? String(bValue).toLowerCase() : '';
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [productosConDestacado, sortColumn, sortDirection]);

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    }
    return sortDirection === 'asc'
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  const confirmarEliminar = async () => {
    if (!productoAEliminar) return;

    setEliminando(true);
    setErrorEliminar(null);

    try {
      if (productoAEliminar.imagenGestionadaPorApi === true) {
        const imagenes = obtenerImagenesProducto(productoAEliminar);
        for (const src of imagenes) {
          if (
            !src ||
            String(src).includes('http') ||
            String(src).includes('firebasestorage')
          ) {
            continue;
          }
          try {
            await eliminarImagen(src);
          } catch (imgError) {
            console.warn('Error al eliminar imagen del producto:', imgError);
          }
        }
      }

      await eliminarProducto(productoAEliminar.id);

      setShowConfirmEliminar(false);
      setProductoAEliminar(null);

      if (onEliminar) {
        onEliminar();
      }
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      setErrorEliminar(error.message || 'Error al eliminar el producto');
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="admin-pedidos-table">
      {errorDestacado && (
        <Alert
          variant="warning"
          className="mb-3"
          onClose={() => setErrorDestacado(null)}
          dismissible
        >
          {errorDestacado}
        </Alert>
      )}
      <div className="admin-destacados-meta" aria-live="polite">
        <span className="admin-destacados-label">Destacados</span>
        <span className={`admin-destacados-frac${limiteDestacados ? ' is-limit' : ''}`}>
          {destacadosCount} / {DESTACADOS_MAX}
        </span>
      </div>
      <Table hover responsive className="admin-table-modern">
        <thead>
          <tr>
            <th
              className="text-start"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('material')}
            >
              Categoría {getSortIcon('material')}
            </th>
            <th
              className="text-start"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('modelo')}
            >
              Medida {getSortIcon('modelo')}
            </th>
            <th
              className="text-start"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('titulo')}
            >
              Título {getSortIcon('titulo')}
            </th>
            <th
              className="text-start"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('precio')}
            >
              Precio {getSortIcon('precio')}
            </th>
            <th
              className="text-center"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('destacado')}
            >
              Destacado {getSortIcon('destacado')}
            </th>
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {productosOrdenados.map((producto) => (
            <tr key={producto.id}>
              <td className="text-start">{labelCajaTipo(producto.material) || '-'}</td>
              <td className="text-start">{producto.modelo || '-'}</td>
              <td className="text-start">{generarTituloProducto(producto)}</td>
              <td className="text-start">{formatPrecio(producto.precio)}</td>
              <td className="text-center">
                <button
                  type="button"
                  className={`admin-destacado-switch${producto.destacado === true ? ' is-on' : ''}${updatingDestacado.has(producto.id) ? ' is-saving' : ''}`}
                  role="switch"
                  aria-checked={producto.destacado === true}
                  aria-label={`Destacado de ${generarTituloProducto(producto)}`}
                  disabled={
                    updatingDestacado.has(producto.id) ||
                    (limiteDestacados && producto.destacado !== true)
                  }
                  title={
                    limiteDestacados && producto.destacado !== true
                      ? `Ya hay ${DESTACADOS_MAX} destacados`
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDestacadoChange(producto, producto.destacado !== true);
                  }}
                >
                  <span className="admin-destacado-switch-track" aria-hidden="true">
                    <span className="admin-destacado-switch-thumb" />
                  </span>
                  <span className="admin-destacado-switch-text">
                    {producto.destacado === true ? 'Sí' : 'No'}
                  </span>
                </button>
              </td>
              <td className="text-center">
                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleEditar(producto.id)}
                    aria-label="Editar producto"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleEliminar(producto)}
                    aria-label="Eliminar producto"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal
        show={showConfirmEliminar}
        onHide={() => {
          if (!eliminando) {
            setShowConfirmEliminar(false);
            setProductoAEliminar(null);
            setErrorEliminar(null);
          }
        }}
        centered
        className="admin-modal-confirm"
      >
        <Modal.Header closeButton className="admin-modal-header">
          <Modal.Title className="admin-modal-title">Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body className="admin-modal-body">
          {errorEliminar ? (
            <Alert variant="danger" className="mb-0">{errorEliminar}</Alert>
          ) : (
            <>
              ¿Está seguro que desea eliminar el producto{' '}
              <strong>
                {productoAEliminar ? generarTituloProducto(productoAEliminar) : ''}
              </strong>
              ?
              <br />
              <small className="text-muted">Esta acción no se puede deshacer.</small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button
            variant="secondary"
            onClick={() => {
              if (!eliminando) {
                setShowConfirmEliminar(false);
                setProductoAEliminar(null);
                setErrorEliminar(null);
              }
            }}
            className="admin-modal-btn"
            disabled={eliminando}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmarEliminar}
            className="admin-modal-btn"
            disabled={eliminando}
          >
            {eliminando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TablaProductos;
